import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { confirmAlert } from 'react-confirm-alert';
import { toast } from 'react-toastify';
import { ToggleSwitch } from '@C/FormElements';
import services from '@C/MyAccount/_utils/data';
import DropLoaderLarge from '@C/Utils/DropLoaderLarge';
import { authHeader } from '@H';
import { authService } from '@S';
import { format } from 'date-fns';
import subscriptionServices from '@C/Director/Packages/_utils/data';
import Slider from 'react-slick';

const settings = {
  dots: false,
  arrows: false,
  infinite: false,
  speed: 500,
  slidesToShow: 3,
  slidesToScroll: 1,
};

const ViewAccount = () => {
  const account_id = authService.currentAccountID;
  const aciveBunk = authService.aciveBunk;
  const [state, setState] = useState({
    currentPlan: 'monthly',
    features: [],
    listVisible: false,
    activePackage: null,
    plans: [],
    selectedDurations: {},
    loading: true,
    editProfile: false,
    editBunk: false,
    isSubmitting: false,
    showPassword: false,
    showSubscriptionPanel: false,
    orderTrackingId: '',
    subscriptionLoading: false,
    subscriptionDetails: {},
    profile: [
      {
        account_id: '',
        mobile: '',
        bunk_name: '',
        gst_no: '',
        email: '',
        first_name: '',
        last_name: '',
        username: '',
        password: '',
        is_password_update: false,
        address: {
          city: '',
          state: '',
          pincode: '',
          full_address: '',
          country: '',
        },
      },
    ],
  });

  useEffect(() => {
    Promise.all([
      getDealersList(),
      getPackages(),
      getAllFeatures(),
      updateBunkDetails(),
    ])
      .then(([dealersListData, packagesData, featuresData, profileData]) => {
        setState((prevState) => ({
          ...prevState,
          profile: dealersListData,
          plans: packagesData,
          features: featuresData,
          subscriptionDetails: profileData.data.subscription_details,
          loading: false,
          showSubscriptionPanel:
            !profileData.data.subscription_details?.is_subscription_expired,
        }));
        if (profileData.data.subscription_details?.is_subscription_expired)
          showSubscriptionAlert();
      })
      .catch((error) => {
        console.error(error);
        setState((prevState) => ({ ...prevState, loading: false }));
      });
  }, []);

  const getDealersList = () => {
    return services
      .getDealersList(account_id)
      .then((response) => response.data.data.results);
  };

  const getPackages = () => {
    return subscriptionServices.getPackages().then((response) => {
      const data = response.data.data.map((plan) => ({
        name: plan.plan,
        description: plan.notes,
        plan_structure: plan.plan_structure,
        prices: plan.plan_structure.reduce((acc, item) => {
          acc[item.plan_type.toLowerCase()] = item.price;
          return acc;
        }, {}),
        features: plan.packages.map((item) => ({
          name: item.module,
          available: true,
        })),
      }));
      const defaultDurations = data.reduce((acc, plan) => {
        acc[plan.name] = 'monthly';
        return acc;
      }, {});
      setState((prevState) => ({
        ...prevState,
        selectedDurations: defaultDurations,
      }));
      return data;
    });
  };

  const getAllFeatures = () => {
    return subscriptionServices.getAllFeatures().then((response) => {
      return response.data.data.map((feature) => ({
        name: feature.module,
        uuid: feature.uuid,
      }));
    });
  };

  const updateBunkDetails = () => {
    const token = authService.currentUserToken;
    const requestOptions = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    };
    return axios
      .get(
        `${process.env.API_URL}/bunk/associate-bunks/?bunk_id=${account_id}`,
        requestOptions,
      )
      .then((response) => response.data);
  };

  const handlePlanSelect = (selectedPlan) => {
    setState((prevState) => ({ ...prevState, subscriptionLoading: true }));
    const selectedStructure = selectedPlan.plan_structure.find(
      (struct) =>
        struct.plan_type.toLowerCase() ===
        state.selectedDurations[selectedPlan.name],
    );

    if (selectedStructure) {
      const headers = authHeader(1);
      axios
        .post(
          `${process.env.API_URL}/subscription/add/`,
          {
            bunk_id: account_id,
            subscription_plan_id: selectedStructure.uuid,
          },
          { headers },
        )
        .then((response) => {
          const data = response.data;
          if (data.is_success) {
            axios
              .post(
                `${process.env.API_URL}/payment/gate-way/`,
                {
                  bunk_id: account_id,
                  subscription_id: data.data[0].subscription_id,
                },
                { headers },
              )
              .then((response) => {
                const data = response.data.data;
                setState((prevState) => ({
                  ...prevState,
                  orderTrackingId: data.order_tracking_id,
                }));
                window.location = data.redirect_url;
              })
              .catch((error) => {
                console.error('Error fetching payment gateway:', error);
              });
          } else {
            console.error('Error subscribing to plan:', data.message);
          }
        })
        .catch((error) => {
          console.error('Error subscribing to plan:', error);
        });
    }
  };

  const toggleProfileEdit = () => {
    setState((prevState) => ({
      ...prevState,
      editProfile: !prevState.editProfile,
    }));
  };

  const toggleBunkEdit = () => {
    setState((prevState) => ({ ...prevState, editBunk: !prevState.editBunk }));
  };

  const togglePasswordUpdate = () => {
    setState((prevState) => {
      const tmp = [...prevState.profile];
      tmp[0].is_password_update = !tmp[0].is_password_update;
      return { ...prevState, profile: tmp };
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setState((prevState) => {
      const tmp = [...prevState.profile];
      tmp[0][name] = value;
      return { ...prevState, profile: tmp };
    });
  };

  const confirmSubmit = (e) => {
    e.preventDefault();
    setState((prevState) => ({ ...prevState, isSubmitting: true }));
    if (state.profile[0].is_password_update) {
      confirmPasswordUpdate(e);
    } else {
      updateDealerProfile(e);
    }
  };

  const confirmPasswordUpdate = (e) => {
    confirmAlert({
      title: 'Confirm password update',
      message: `You are updating your password which will log you out from this device once updated. Are you sure you want to continue?`,
      buttons: [
        {
          label: 'Yes',
          onClick: () => updateDealerProfile(e),
        },
        {
          label: 'No',
          onClick: () => false,
        },
      ],
    });
  };

  const updateDealerProfile = (e) => {
    e.preventDefault();
    setState((prevState) => ({ ...prevState, isSubmitting: true }));
    const formData = {
      account_id: state.profile[0].account_id,
      mobile: state.profile[0].contact,
      bunk_name: state.profile[0].name,
      gst_no: state.profile[0].gst_no,
      email: state.profile[0].email,
      first_name: state.profile[0].dealer_first_name,
      last_name: state.profile[0].dealer_last_name,
      username: state.profile[0].username,
      is_password_update: state.profile[0].is_password_update,
      password: state.profile[0].password,
      address: {
        city: state.profile[0].city,
        state: state.profile[0].state,
        pincode: state.profile[0].pincode,
        full_address: state.profile[0].full_address,
        country: state.profile[0].country,
      },
    };
    const headers = authHeader(1);
    axios
      .put(`${process.env.API_URL}/users/update-dealer/`, formData, { headers })
      .then((res) => {
        toast.success('Account details updated successfully');
        setState((prevState) => ({
          ...prevState,
          editProfile: false,
          isSubmitting: false,
        }));
        if (formData.is_password_update) {
          authService.logout();
          window.location = '/';
        } else {
          getDealersList().then((dealersListData) => {
            setState((prevState) => ({
              ...prevState,
              profile: dealersListData,
            }));
          });
        }
      })
      .catch((error) => {
        setState((prevState) => ({ ...prevState, isSubmitting: false }));
        console.log(error);
      });
  };

  const showSubscriptionAlert = () => {
    confirmAlert({
      customUI: ({ onClose }) => {
        return (
          <div className="react-confirm-alert-body">
            <h1 style={{ color: '#ed4545' }}>Subscription Expired!</h1>
            Your subscription has expired. Please renew your subscription to
            continue using the services.
            <div className="react-confirm-alert-button-group">
              <button label="Yes" onClick={onClose}>
                Okay
              </button>
            </div>
          </div>
        );
      },
    });
  };

  if (state.loading) {
    return <DropLoaderLarge />;
  } else {
    return (
      <>
        <section className="widget form-card">
          <header>
            <div className="actions space-between flex-1">
              <h2>Personal Information</h2>
              <button
                className={
                  !state.editProfile
                    ? `white-btn btn-with-icon btn-with-icon__edit action-btn action-btn`
                    : `btn-with-icon btn-with-icon__cancel-edit action-btn`
                }
                onClick={() => toggleProfileEdit()}
              >
                {`${state.editProfile ? 'Cancel' : 'Edit'}`}
              </button>
            </div>
          </header>
          <main>
            <form className="inline">
              {state.profile.map((item, index) => {
                return (
                  <React.Fragment key={index}>
                    <div className="input-wrapper with-label">
                      <label>First name</label>
                      <input
                        type="text"
                        onChange={handleChange}
                        value={item.dealer_first_name}
                        name="dealer_first_name"
                        disabled={!state.editProfile}
                      />
                    </div>
                    <div className="input-wrapper with-label">
                      <label>Last name</label>
                      <input
                        type="text"
                        onChange={handleChange}
                        value={item.dealer_last_name}
                        name="dealer_last_name"
                        disabled={!state.editProfile}
                      />
                    </div>
                    <div className="input-wrapper with-label">
                      <label>Email</label>
                      <input
                        type="email"
                        onChange={handleChange}
                        value={item.email}
                        name="email"
                        disabled={!state.editProfile}
                      />
                    </div>
                    <div className="input-wrapper with-label">
                      <label>Mobile</label>
                      <input
                        type="number"
                        value={item.contact}
                        name="contact"
                        disabled={true}
                      />
                    </div>
                    <div className="input-wrapper with-label">
                      <label>Username</label>
                      <input
                        type="text"
                        onChange={handleChange}
                        value={item.username}
                        name="username"
                        disabled={!state.editProfile}
                      />
                    </div>
                    <div className="input-wrapper  toggle-element">
                      <span className="label"> Update password: </span>
                      <ToggleSwitch
                        did={0}
                        id="updatePassword"
                        name="updatePassword"
                        disabled={!state.editProfile}
                        checked={item.is_password_update}
                        onChange={togglePasswordUpdate}
                      />
                    </div>
                    {item.is_password_update && editProfile && (
                      <div
                        className={
                          showPassword
                            ? 'input-wrapper visible with-label'
                            : 'input-wrapper with-label'
                        }
                      >
                        <label> Password </label>
                        <input
                          type={state.showPassword ? 'text' : 'password'}
                          value={item.password}
                          onChange={handleChange}
                          name="password"
                        />
                        <span
                          className="show-psswd"
                          onClick={() =>
                            setState((prevState) => ({
                              ...prevState,
                              showPassword: !state.showPassword,
                            }))
                          }
                        />
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
              {state.editProfile && (
                <div className="cta-wrapper">
                  <button
                    className={`submit ${state.isSubmitting ? 'loading' : ''}`}
                    onClick={(e) => confirmSubmit(e)}
                  >
                    Submit
                  </button>
                </div>
              )}
            </form>
          </main>
        </section>
        <section className="widget form-card">
          <header>
            <div className="actions space-between flex-1">
              <h2>Bunk Information</h2>
              <button
                className={
                  !state.editBunk
                    ? `white-btn btn-with-icon btn-with-icon__edit action-btn`
                    : `btn-with-icon btn-with-icon__cancel-edit action-btn`
                }
                onClick={() => {
                  toggleBunkEdit();
                }}
              >
                {`${state.editBunk ? 'Cancel' : 'Edit'}`}
              </button>
            </div>
          </header>
          <main>
            <form className="inline">
              {state.profile.map((item, index) => {
                return (
                  <React.Fragment key={index}>
                    <div className="input-wrapper with-label">
                      <label>Bunk name</label>
                      <input
                        type="text"
                        onChange={handleChange}
                        value={item.name}
                        name="name"
                        disabled={!state.editBunk}
                      />
                    </div>
                    <div className="input-wrapper with-label">
                      <label>OMC</label>
                      <input
                        disabled={true}
                        value={item.omc_name}
                        name="omc_name"
                      />
                    </div>
                    <div className="input-wrapper with-label">
                      <label>GST </label>
                      <input
                        type="text"
                        onChange={handleChange}
                        value={item.gst_no}
                        name="gst_no"
                        disabled={!state.editBunk}
                      />
                    </div>
                    <div className="input-wrapper with-label">
                      <label>Pincode</label>
                      <input
                        value={item.pin_code}
                        name="pin_code"
                        disabled={true}
                      />
                    </div>
                    <div className="input-wrapper with-label">
                      <label>City</label>
                      <input value={item.city} name="city" disabled={true} />
                    </div>
                    <div className="input-wrapper with-label">
                      <label>State</label>
                      <input value={item.state} name="state" disabled={true} />
                    </div>
                    <div className="input-wrapper with-label">
                      <label>Country</label>
                      <input
                        value={item.country}
                        disabled={true}
                        name="country"
                      />
                    </div>
                    <div className="input-wrapper with-label">
                      <label>Full address </label>
                      <input
                        type="text"
                        onChange={handleChange}
                        value={item.full_address}
                        name="full_address"
                        disabled={!state.editBunk}
                      />
                    </div>
                  </React.Fragment>
                );
              })}
              {state.editBunk && (
                <div className="cta-wrapper">
                  <button
                    className={`submit ${state.isSubmitting ? 'loading' : ''}`}
                    onClick={(e) => confirmSubmit(e)}
                  >
                    Submit
                  </button>
                </div>
              )}
            </form>
          </main>
        </section>
        <section className="widget form-card">
          <header>
            <div className="actions space-between flex-1">
              <h2>Subscription Information</h2>
            </div>
          </header>
          <main>
            <div className="info-contianer">
              <p>
                <span>Status: </span>
                <span>
                  {state.subscriptionDetails?.is_subscription_expired
                    ? 'Expired'
                    : 'Active'}
                </span>
              </p>
              <p>
                <span>Expiry date:</span>
                <span>
                  {state.subscriptionDetails?.subscription_expiry_date
                    ? format(
                        state.subscriptionDetails?.subscription_expiry_date,
                        'dd MMMM yyyy',
                      )
                    : 'N/A'}
                </span>
              </p>
              <p>
                <span>Subscription type:</span>
                <span>{state.subscriptionDetails?.package_name}</span>
              </p>
              <p>
                <span>Renewal period:</span>
                <span>{state.subscriptionDetails?.plan_name}</span>
              </p>
              <p>
                <button
                  className="submit"
                  onClick={() =>
                    setState((prevState) => ({
                      ...prevState,
                      showSubscriptionPanel: !state.showSubscriptionPanel,
                    }))
                  }
                >
                  {state.subscriptionDetails?.is_subscription_expired
                    ? 'Renew'
                    : 'Upgrade/Downgrade Subscription'}
                </button>
              </p>
            </div>
          </main>
        </section>
        {state.showSubscriptionPanel && (
          <section className="widget form-card full-width">
            <header>
              <div className="actions space-between flex-1">
                <h2>Subscription Plans</h2>
              </div>
            </header>
            <main>
              <div className="subscription-container" style={{ width: '100%' }}>
                {state.subscriptionLoading && (
                  <div
                    className="overlay"
                    style={{
                      position: 'absolute',
                      background: '#ffffffd9',
                      zIndex: '99',
                      width: '100%',
                      height: '100%',
                    }}
                  >
                    <DropLoaderLarge />
                  </div>
                )}
                <div className="subscription-plans">
                  <div className="subscription-header">
                    <div className="plan-switcher">
                      <button
                        className={
                          state.currentPlan === 'monthly' ? 'active' : ''
                        }
                        onClick={() =>
                          setState((prevState) => ({
                            ...prevState,
                            currentPlan: 'monthly',
                          }))
                        }
                      >
                        Monthly
                      </button>
                      <button
                        className={
                          state.currentPlan === 'quarterly' ? 'active' : ''
                        }
                        onClick={() =>
                          setState((prevState) => ({
                            ...prevState,
                            currentPlan: 'quarterly',
                          }))
                        }
                      >
                        Quarterly
                      </button>
                      <button
                        className={
                          state.currentPlan === 'yearly' ? 'active' : ''
                        }
                        onClick={() =>
                          setState((prevState) => ({
                            ...prevState,
                            currentPlan: 'yearly',
                          }))
                        }
                      >
                        Yearly
                      </button>
                      <div className="highlighter"></div>
                    </div>
                  </div>
                  <div className="subscription-content">
                    <div className="features-container">
                      <div className="features">
                        <h3>Features</h3>
                        <ul>
                          {state.features.map((feature) => (
                            <li
                              key={feature.uuid}
                              className={`${state.listVisible ? 'list-visible' : ''}`}
                            >
                              {feature.name}
                            </li>
                          ))}
                        </ul>
                        <button
                          className="show-more"
                          onClick={() =>
                            setState((prevState) => ({
                              ...prevState,
                              listVisible: !state.listVisible,
                            }))
                          }
                        >
                          {state.listVisible ? 'Show Less' : 'Show More'}
                        </button>
                      </div>
                    </div>
                    <Slider {...settings} className="plan-container">
                      {state.plans.map((plan) => (
                        <div
                          className={`plan-item ${state.activePackage === plan.name ? 'active' : ''}`}
                          key={plan.name}
                          onClick={() =>
                            setState((prevState) => ({
                              ...prevState,
                              activePackage: plan.name,
                            }))
                          }
                        >
                          <h3>
                            {plan.name} {plan.tag && <span>({plan.tag})</span>}
                          </h3>
                          <p>{plan.description}</p>

                          <div className="price-box">
                            <div className="price-wrapper">
                              ${plan.prices[state.currentPlan] || 'N/A'}
                              <span>
                                {state.currentPlan === 'monthly' && '/month'}
                                {state.currentPlan === 'quarterly' &&
                                  '/quarter'}
                                {state.currentPlan === 'yearly' && '/year'}
                              </span>
                            </div>
                          </div>
                          <div className="plan-features">
                            <ul>
                              {state.features.map((feature) => {
                                const isAvailable = plan.features.some(
                                  (planFeature) =>
                                    planFeature.name === feature.name,
                                );

                                return (
                                  <li
                                    key={feature.uuid}
                                    className={`${state.listVisible ? 'list-visible' : ''}`}
                                  >
                                    <span
                                      className={`availablity ${isAvailable ? 'available' : 'unavailable'}`}
                                    ></span>
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                          <div className="plan-actions">
                            <button
                              className="submit"
                              onClick={() => handlePlanSelect(plan)}
                            >
                              Select Plan
                            </button>
                          </div>
                        </div>
                      ))}
                    </Slider>
                  </div>
                </div>
              </div>
            </main>
          </section>
        )}
      </>
    );
  }
};
export { ViewAccount };
